import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
  GROUP_TOKEN_SEPARATOR,
  STATES_QUERY_PARAM,
  VARIANTS_QUERY_PARAM,
} from "./consts";
import { buildGroupQueryKey } from "./helpers";
import { StateGroup, VariantGroup } from "./types";

export const useParamsSync = (
  variantGroups: VariantGroup[],
  stateGroups: StateGroup[]
) => {
  const location = useLocation();

  const lastHydratedSearchRef = useRef<string | null>(null);
  const variantDefaultByGroupKeyRef = useRef(new Map<string, number>());
  const stateDefaultByGroupKeyRef = useRef(new Map<string, number>());

  useEffect(() => {
    variantGroups.forEach((group) => {
      const groupKey = buildGroupQueryKey(group);
      if (!variantDefaultByGroupKeyRef.current.has(groupKey)) {
        variantDefaultByGroupKeyRef.current.set(groupKey, group.active);
      }
    });

    stateGroups.forEach((group) => {
      const groupKey = buildGroupQueryKey(group);
      if (!stateDefaultByGroupKeyRef.current.has(groupKey)) {
        stateDefaultByGroupKeyRef.current.set(groupKey, group.active);
      }
    });

    if (lastHydratedSearchRef.current === location.search) {
      return;
    }
    lastHydratedSearchRef.current = location.search;

    const params = new URLSearchParams(location.search);
    const variantSelections = parseSelectionTokens(
      parseParamValues(params, VARIANTS_QUERY_PARAM)
    );
    const stateSelections = parseSelectionTokens(
      parseParamValues(params, STATES_QUERY_PARAM)
    );

    variantGroups.forEach((group) => {
      const groupKey = buildGroupQueryKey(group);
      let nextVariantValue = variantSelections.get(groupKey);

      // Legacy shorthand: ?layout=N → layout group variant N
      if (nextVariantValue == null && groupKey === "layout") {
        const legacyLayout = params.get("layout");
        if (legacyLayout != null && legacyLayout.length > 0) {
          nextVariantValue = legacyLayout;
        }
      }
      const parsedVariant =
        nextVariantValue == null ? null : Number(nextVariantValue);
      const hasValidRequestedVariant =
        parsedVariant != null &&
        Number.isInteger(parsedVariant) &&
        group.variants.some(
          (variant) => variant.variantNumber === parsedVariant
        );
      const fallbackVariant =
        variantDefaultByGroupKeyRef.current.get(groupKey) ?? group.active;
      const targetVariant = hasValidRequestedVariant
        ? parsedVariant
        : fallbackVariant;

      if (group.active !== targetVariant) {
        group.onChange(targetVariant);
      }
    });

    stateGroups.forEach((group) => {
      const groupKey = buildGroupQueryKey(group);
      const nextStateValue = stateSelections.get(groupKey);
      const parsedState =
        nextStateValue == null ? null : Number(nextStateValue);
      const hasValidRequestedState =
        parsedState != null &&
        Number.isInteger(parsedState) &&
        group.states.some(
          (stateOption) => stateOption.stateNumber === parsedState
        );
      const fallbackState =
        stateDefaultByGroupKeyRef.current.get(groupKey) ?? group.active;
      const targetState = hasValidRequestedState ? parsedState : fallbackState;

      if (group.active !== targetState) {
        group.onChange(targetState);
      }
    });
  }, [location.search, stateGroups, variantGroups]);
};

const parseParamValues = (
  params: URLSearchParams,
  paramName: string
): string[] =>
  params
    .getAll(paramName)
    .flatMap((entry) => entry.split(","))
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

const parseSelectionTokens = (tokens: string[]): Map<string, string> => {
  const parsed = new Map<string, string>();
  tokens.forEach((token) => {
    const separatorIndex = token.indexOf(GROUP_TOKEN_SEPARATOR);
    if (separatorIndex <= 0 || separatorIndex === token.length - 1) {
      return;
    }
    const groupKey = token.slice(0, separatorIndex);
    const value = token.slice(separatorIndex + 1);
    parsed.set(groupKey, value);
  });
  return parsed;
};
